import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Image,
  Modal,
  Spin,
  Tooltip,
  Typography,
} from 'antd';
import {
  DeleteOutlined,
  DownOutlined,
  DownloadOutlined,
  FileImageOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { JobFlyerService } from '../services/job-flyer.service';
import {
  JobFlyerCopy,
  JobFlyerGenerateResponse,
} from '../models/job-flyer.model';
import {
  DEFAULT_FLYER_OPTION_ID,
  FLYER_PALETTE_OPTIONS,
  JobFlyerPaletteOption,
  flyerOptionLabel,
  isLightFlyerOption,
  mergeFlyerOptions,
  resolveFlyerOptionId,
} from '../models/job-flyer-options';
import {
  DEFAULT_FLYER_LAYOUT_ID,
  FLYER_COMPOSITION_OPTIONS,
  JobFlyerCompositionOption,
  flyerLayoutLabel,
  mergeFlyerLayouts,
  resolveFlyerLayoutId,
} from '../models/job-flyer-layouts';
import FlyerLayoutOptionSwatch from './FlyerLayoutOptionSwatch';
import FlyerCompositionSwatch from './FlyerCompositionSwatch';
import { UtilService } from '../../../../../core/services/util.service';

interface JobFlyerPanelProps {
  jobId?: string;
  isDeleted?: boolean;
}

const FLYER_THUMB_H = 52;
const FLYER_THUMB_W = Math.round(FLYER_THUMB_H * (1080 / 1350));

const toPreviewBlob = (blob: unknown): Blob => {
  if (blob instanceof Blob && blob.size > 0) {
    return blob;
  }
  throw new Error('Flyer PNG download returned an empty or invalid response.');
};

const JobFlyerPanel: React.FC<JobFlyerPanelProps> = ({ jobId, isDeleted }) => {
  const canGenerate = Boolean(jobId);
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [metadataLoading, setMetadataLoading] = useState(Boolean(jobId));
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [metadata, setMetadata] = useState<JobFlyerGenerateResponse | null>(
    null
  );
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const previewUrlRef = useRef<string | null>(null);
  const [palettes, setPalettes] = useState<JobFlyerPaletteOption[]>(
    FLYER_PALETTE_OPTIONS
  );
  const [layouts, setLayouts] = useState<JobFlyerCompositionOption[]>(
    FLYER_COMPOSITION_OPTIONS
  );
  const [selectedThemeId, setSelectedThemeId] = useState(
    DEFAULT_FLYER_OPTION_ID
  );
  const [selectedLayoutId, setSelectedLayoutId] = useState(
    DEFAULT_FLYER_LAYOUT_ID
  );

  const replacePreview = useCallback((nextUrl: string | null) => {
    if (previewUrlRef.current) {
      window.URL.revokeObjectURL(previewUrlRef.current);
    }
    previewUrlRef.current = nextUrl;
    setPreviewUrl(nextUrl);
  }, []);

  const refreshPreview = useCallback(async () => {
    if (!jobId) return false;
    const blob = toPreviewBlob(await JobFlyerService.DownloadPng(jobId));
    replacePreview(window.URL.createObjectURL(blob));
    setPreviewError(null);
    return true;
  }, [jobId, replacePreview]);

  useEffect(
    () => () => {
      if (previewUrlRef.current) {
        window.URL.revokeObjectURL(previewUrlRef.current);
      }
    },
    []
  );

  useEffect(() => {
    if (!jobId) {
      setMetadataLoading(false);
      return;
    }
    setMetadata(null);
    replacePreview(null);
    setPreviewError(null);
    const loadMetadata = async () => {
      setMetadataLoading(true);
      try {
        const existing = await JobFlyerService.GetMetadata(jobId);
        if (!existing?.fileId) return;
        setMetadata(existing);
        setSelectedThemeId(resolveFlyerOptionId(existing.theme));
        setSelectedLayoutId(resolveFlyerLayoutId(existing.layout));
      } catch {
        // No flyer yet.
      } finally {
        setMetadataLoading(false);
      }
    };
    void loadMetadata();
  }, [jobId, replacePreview]);

  useEffect(() => {
    const loadCatalogs = async () => {
      setCatalogLoading(true);
      await Promise.all([
        JobFlyerService.ListPalettes()
          .then((catalog) => setPalettes(mergeFlyerOptions(catalog ?? [])))
          .catch(() => setPalettes(FLYER_PALETTE_OPTIONS)),
        JobFlyerService.ListLayouts()
          .then((catalog) => setLayouts(mergeFlyerLayouts(catalog ?? [])))
          .catch(() => setLayouts(FLYER_COMPOSITION_OPTIONS)),
      ]);
      setCatalogLoading(false);
    };
    void loadCatalogs();
  }, []);

  useEffect(() => {
    if (!metadata?.fileId || previewUrl || generating || loading) return;
    void refreshPreview().catch((error: unknown) => {
      console.error('Flyer preview load failed:', error);
      setPreviewError('Preview unavailable — try Refresh content.');
    });
  }, [generating, loading, metadata?.fileId, previewUrl, refreshPreview]);

  const handleFlyerAction = async (
    refresh: boolean,
    intent: 'create' | 'design' | 'refresh'
  ) => {
    if (!jobId || generating) return;
    setGenerating(true);
    setPreviewError(null);
    try {
      const response = await JobFlyerService.Generate(jobId, {
        refresh,
        theme: selectedThemeId,
        layout: selectedLayoutId,
      });
      if (!response?.fileId) {
        throw new Error('Flyer generation did not return a file reference.');
      }
      setMetadata(response);
      setSelectedThemeId(resolveFlyerOptionId(response.theme));
      setSelectedLayoutId(resolveFlyerLayoutId(response.layout));
      try {
        await refreshPreview();
        setExpanded(true);
        const successMessage =
          intent === 'refresh'
            ? 'Content and role-specific artwork refreshed from the job description.'
            : intent === 'design'
              ? 'Flyer updated with the selected composition and palette.'
              : 'Role-specific flyer generated.';
        UtilService.Alert('Flyer ready', 'success', successMessage);
      } catch (previewFailure: unknown) {
        console.error(
          'Flyer preview load failed after generate:',
          previewFailure
        );
        setExpanded(true);
        setPreviewError(
          'Flyer saved but preview failed — try Download or Refresh content.'
        );
        UtilService.Alert(
          'Flyer saved',
          'warning',
          'Generation succeeded, but the preview could not be loaded.'
        );
      }
    } catch (error: unknown) {
      console.error('Flyer generation failed:', error);
      UtilService.Alert(
        'Flyer unavailable',
        'error',
        error instanceof Error && error.message
          ? error.message
          : 'Failed to generate job flyer.'
      );
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async () => {
    if (!jobId || generating) return;
    setLoading(true);
    try {
      const blob = toPreviewBlob(await JobFlyerService.DownloadPng(jobId));
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `job-flyer-${jobId}.png`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: unknown) {
      console.error('Flyer download failed:', error);
      UtilService.Alert(
        'Download unavailable',
        'error',
        'Failed to download the job flyer.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    if (!jobId || !metadata || generating) return;
    Modal.confirm({
      title: 'Delete Social Job Flyer?',
      content: 'Removes the PNG and cached flyer content.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        setLoading(true);
        try {
          await JobFlyerService.Delete(jobId);
          replacePreview(null);
          setMetadata(null);
          setPreviewError(null);
          setExpanded(false);
          UtilService.Alert(
            'Flyer removed',
            'success',
            'Social job flyer deleted.'
          );
        } catch (error: unknown) {
          console.error('Flyer delete failed:', error);
          UtilService.Alert(
            'Delete unavailable',
            'error',
            'Failed to delete the job flyer.'
          );
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const copy: JobFlyerCopy | undefined = metadata?.copy;
  const isReady = Boolean(metadata?.fileId && !previewError);
  const hasStoredFlyer = Boolean(metadata?.fileId);
  const storedThemeId = resolveFlyerOptionId(metadata?.theme);
  const storedLayoutId = resolveFlyerLayoutId(metadata?.layout);
  const designPendingApply =
    hasStoredFlyer &&
    (selectedThemeId !== storedThemeId || selectedLayoutId !== storedLayoutId);
  const initializing = metadataLoading || catalogLoading;
  const controlsDisabled =
    initializing || generating || loading || Boolean(isDeleted);

  const statusBadge = (
    <span role="status" aria-live="polite">
      {initializing ? (
        <Badge status="processing" text="Loading" />
      ) : generating ? (
        <Badge status="processing" text="Generating" />
      ) : loading ? (
        <Badge status="processing" text="Working" />
      ) : isReady ? (
        <Badge status="success" text="Ready" />
      ) : hasStoredFlyer ? (
        <Badge status="warning" text="No preview" />
      ) : null}
    </span>
  );

  const collapsedSummary = isReady
    ? `${copy?.headline ?? 'Portrait PNG'} · ${flyerLayoutLabel(
        storedLayoutId
      )} · ${flyerOptionLabel(storedThemeId)}`
    : previewError
      ? previewError
      : hasStoredFlyer
        ? (copy?.headline ?? 'Flyer on file')
        : canGenerate
          ? 'Job-driven 1080×1350 campaign flyer'
          : 'Save the job first';

  const renderThumb = () => {
    if (previewUrl) {
      return (
        <img
          src={previewUrl}
          alt=""
          width={FLYER_THUMB_W}
          height={FLYER_THUMB_H}
          className="shrink-0 rounded border border-gray-200 object-cover"
        />
      );
    }
    if (initializing || (hasStoredFlyer && !previewError)) {
      return (
        <div
          className="flex shrink-0 items-center justify-center rounded border border-dashed border-gray-300 bg-gray-50"
          style={{ width: FLYER_THUMB_W, height: FLYER_THUMB_H }}
          role="status"
          aria-label="Loading Flyer Studio"
        >
          <Spin size="small" />
        </div>
      );
    }
    return (
      <div
        className="flex shrink-0 items-center justify-center rounded border border-dashed border-gray-300 bg-gray-50 text-gray-400"
        style={{ width: FLYER_THUMB_W, height: FLYER_THUMB_H }}
        aria-hidden
      >
        <FileImageOutlined />
      </div>
    );
  };

  const renderLayoutCard = (option: JobFlyerCompositionOption) => {
    const selected = selectedLayoutId === option.id;
    return (
      <Tooltip key={option.id} title={`Best for: ${option.bestFor}`}>
        <button
          type="button"
          role="radio"
          aria-checked={selected}
          disabled={controlsDisabled}
          onClick={() => setSelectedLayoutId(option.id)}
          className={[
            'flex min-h-[76px] items-center gap-2.5 rounded-lg border p-2.5 text-left transition-all',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500',
            selected
              ? 'border-blue-500 bg-blue-50 shadow-sm ring-1 ring-blue-500'
              : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/40',
            controlsDisabled
              ? 'cursor-not-allowed opacity-60'
              : 'cursor-pointer',
          ].join(' ')}
        >
          <FlyerCompositionSwatch layoutId={option.id} />
          <span className="min-w-0">
            <strong className="block text-xs text-gray-900">
              {option.optionCode} · {option.label}
            </strong>
            <span className="mt-0.5 block text-[10px] leading-snug text-gray-500">
              {option.description}
            </span>
          </span>
        </button>
      </Tooltip>
    );
  };

  const renderThemeChip = (option: JobFlyerPaletteOption) => {
    const selected = selectedThemeId === option.id;
    return (
      <Tooltip
        key={option.id}
        title={
          <span className="text-xs">
            {option.description}
            <br />
            <span className="text-gray-300">Best for: {option.bestFor}</span>
          </span>
        }
      >
        <button
          type="button"
          role="radio"
          aria-checked={selected}
          disabled={controlsDisabled}
          onClick={() => setSelectedThemeId(option.id)}
          className={[
            'inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-left transition-colors',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-500',
            selected
              ? 'border-blue-500 bg-blue-50 text-blue-900 ring-1 ring-blue-500'
              : 'border-gray-200 bg-white text-gray-800 hover:border-gray-300',
            controlsDisabled
              ? 'cursor-not-allowed opacity-60'
              : 'cursor-pointer',
          ].join(' ')}
        >
          <FlyerLayoutOptionSwatch optionId={option.id} compact />
          <span className="whitespace-nowrap text-xs font-medium">
            {option.optionCode} · {option.label}
          </span>
        </button>
      </Tooltip>
    );
  };

  const renderThemeGroup = (
    label: string,
    options: JobFlyerPaletteOption[],
    ariaLabel: string
  ) =>
    options.length ? (
      <div className="space-y-1.5">
        <Typography.Text className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
          {label}
        </Typography.Text>
        <div
          className="flex flex-wrap gap-1.5"
          role="radiogroup"
          aria-label={ariaLabel}
        >
          {options.map(renderThemeChip)}
        </div>
      </div>
    ) : null;

  const renderCopySummary = () => (
    <div className="min-w-0 flex-1 space-y-2 text-sm">
      <p className="m-0 font-semibold leading-snug text-gray-900">
        {copy?.headline}
      </p>
      {copy?.tagline ? (
        <p className="m-0 text-xs leading-relaxed text-gray-600">
          {copy.tagline}
        </p>
      ) : null}
      {copy?.skills?.length ? (
        <div className="flex flex-wrap gap-1">
          {copy.skills.slice(0, 3).map((skill) => (
            <span
              key={skill}
              className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700"
            >
              {skill}
            </span>
          ))}
        </div>
      ) : null}
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-gray-500">
        <span>Layout: {flyerLayoutLabel(metadata?.layout)}</span>
        <span>Palette: {flyerOptionLabel(metadata?.theme)}</span>
        {metadata?.fromCache ? <span>Content cached</span> : null}
      </div>
      <p className="m-0 text-[11px] leading-relaxed text-gray-500">
        Artwork responds to the role description. Job text, skills, application
        URL, and QR code remain deterministic.
      </p>
    </div>
  );

  const renderPreview = () => (
    <div className="flex shrink-0 flex-col items-center gap-2">
      {previewUrl ? (
        <Image
          src={previewUrl}
          alt="Job flyer preview"
          className="rounded-lg border border-gray-200 shadow-sm"
          style={{ maxHeight: 420, maxWidth: 336 }}
        />
      ) : metadata?.fileId && previewError ? (
        <Typography.Text type="warning" className="text-xs">
          {previewError}
        </Typography.Text>
      ) : metadata?.fileId ? (
        <Spin size="small" />
      ) : null}
    </div>
  );

  const darkPalettes = palettes.filter((option) => !isLightFlyerOption(option));
  const lightPalettes = palettes.filter((option) => isLightFlyerOption(option));

  return (
    <>
      <Card
        className="mt-4"
        size="small"
        bodyStyle={{ padding: '10px 14px' }}
        aria-busy={initializing || generating || loading}
      >
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <button
            type="button"
            className="flex min-w-0 flex-1 items-center gap-2 rounded border-0 bg-transparent p-0 text-left hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
            onClick={() => setExpanded((open) => !open)}
            aria-expanded={expanded}
            aria-controls="job-flyer-details"
            disabled={initializing || generating}
          >
            {renderThumb()}
            <span className="min-w-0 flex-1">
              <span className="flex flex-wrap items-center gap-1.5">
                <Typography.Text strong className="text-xs sm:text-sm">
                  Flyer Studio
                </Typography.Text>
                {statusBadge}
              </span>
              <Typography.Text
                type="secondary"
                className="block text-[11px] leading-tight line-clamp-1 sm:text-xs"
              >
                {collapsedSummary}
              </Typography.Text>
            </span>
            <DownOutlined
              aria-hidden
              className="shrink-0 text-xs text-gray-400 transition-transform"
              style={{ transform: expanded ? 'rotate(180deg)' : undefined }}
            />
          </button>

          {!isDeleted && !hasStoredFlyer ? (
            <Button
              type="primary"
              size="small"
              icon={<FileImageOutlined />}
              onClick={() => void handleFlyerAction(false, 'create')}
              loading={generating}
              disabled={initializing || generating || !canGenerate}
            >
              Generate
            </Button>
          ) : !isDeleted && !expanded ? (
            <Button
              type="text"
              size="small"
              onClick={() => setExpanded(true)}
              disabled={generating}
            >
              Customize
            </Button>
          ) : null}
        </div>

        {expanded ? (
          <div
            id="job-flyer-details"
            className="mt-3 space-y-4 border-t border-gray-100 pt-3"
          >
            {!isDeleted ? (
              <>
                <section className="space-y-2">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <Typography.Text className="block text-[11px] font-semibold uppercase tracking-wide text-gray-600">
                        Composition
                      </Typography.Text>
                      <Typography.Text
                        type="secondary"
                        className="block text-[11px]"
                      >
                        Select how the job description becomes a visual story.
                      </Typography.Text>
                    </div>
                    {designPendingApply ? (
                      <Button
                        type="primary"
                        size="small"
                        icon={<FileImageOutlined />}
                        onClick={() => void handleFlyerAction(false, 'design')}
                        loading={generating}
                        disabled={controlsDisabled}
                      >
                        Apply design
                      </Button>
                    ) : null}
                  </div>
                  <div
                    className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3"
                    role="radiogroup"
                    aria-label="Flyer composition"
                  >
                    {layouts.map(renderLayoutCard)}
                  </div>
                </section>

                <section className="space-y-2 border-t border-gray-100 pt-3">
                  <div>
                    <Typography.Text className="block text-[11px] font-semibold uppercase tracking-wide text-gray-600">
                      Color palette
                    </Typography.Text>
                    <Typography.Text
                      type="secondary"
                      className="block text-[11px]"
                    >
                      Choose independently from the composition.
                    </Typography.Text>
                  </div>
                  {renderThemeGroup(
                    'Dark & expressive',
                    darkPalettes,
                    'Dark and expressive flyer palettes'
                  )}
                  {renderThemeGroup(
                    'Light & editorial',
                    lightPalettes,
                    'Light and editorial flyer palettes'
                  )}
                  {designPendingApply ? (
                    <Typography.Text type="secondary" className="text-[11px]">
                      Apply design re-renders the PNG while keeping the current
                      extracted job content.
                    </Typography.Text>
                  ) : null}
                </section>
              </>
            ) : null}

            {!isDeleted ? (
              <div className="flex flex-wrap items-center gap-1.5 border-t border-gray-100 pt-3">
                {metadata ? (
                  <>
                    <Tooltip title="Re-read the job description, refresh the role-specific artwork, and render the current design.">
                      <Button
                        size="small"
                        icon={<ReloadOutlined />}
                        onClick={() => void handleFlyerAction(true, 'refresh')}
                        loading={generating}
                        disabled={controlsDisabled || !canGenerate}
                      >
                        Refresh content
                      </Button>
                    </Tooltip>
                    <Button
                      size="small"
                      icon={<DownloadOutlined />}
                      onClick={() => void handleDownload()}
                      loading={loading}
                      disabled={generating || !metadata?.fileId}
                    >
                      Download PNG
                    </Button>
                    <Button
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={handleDelete}
                      disabled={controlsDisabled || !canGenerate}
                    >
                      Delete
                    </Button>
                  </>
                ) : (
                  <Button
                    type="primary"
                    size="small"
                    icon={<FileImageOutlined />}
                    onClick={() => void handleFlyerAction(false, 'create')}
                    loading={generating}
                    disabled={controlsDisabled || !canGenerate}
                  >
                    Generate flyer
                  </Button>
                )}
              </div>
            ) : null}

            {generating && !metadata ? (
              <div className="flex justify-center py-4" role="status">
                <Spin size="small" tip="Generating…" />
              </div>
            ) : null}

            {copy ? (
              <div className="flex flex-col gap-4 rounded-lg bg-slate-50 p-3 sm:flex-row sm:items-start">
                {renderPreview()}
                {renderCopySummary()}
              </div>
            ) : (
              !generating && (
                <Typography.Text type="secondary" className="text-xs">
                  {canGenerate
                    ? 'Choose a composition and palette, then generate a 1080×1350 social flyer.'
                    : 'Save the job first, then generate a flyer.'}
                </Typography.Text>
              )
            )}
          </div>
        ) : null}
      </Card>

      <Modal
        open={generating}
        title="Generating job flyer"
        footer={null}
        closable={false}
        maskClosable={false}
        keyboard={false}
        centered
        destroyOnClose
        aria-busy={generating}
      >
        <div className="flex flex-col items-center gap-3 py-4" role="status">
          <Spin />
          <Typography.Paragraph
            type="secondary"
            className="!mb-0 text-center text-sm"
          >
            Reading the job description, preparing role-specific artwork, and
            rendering the final PNG. This may take up to a minute.
          </Typography.Paragraph>
        </div>
      </Modal>
    </>
  );
};

export default JobFlyerPanel;
